import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
  useMemo,
  useRef,
} from 'react';
import classNames from 'classnames';
import { DraftContact } from 'models/Contact';
import { SideDrawerRef } from 'router/subrouters/DataRoom/components/SideDrawer/SideDrawer.component';
import useCallbackRef from 'hooks/useCallbackRef';
import SideDrawer from 'router/subrouters/DataRoom/components/SideDrawer';
import { Button, Loader } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import { FormApi } from 'final-form';
import { useTranslation } from 'react-i18next';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import InputField from 'components/InputField';
import useValidations from 'router/subrouters/Auth/hooks/useValidations';
import PhoneFieldWrapper from 'components/PhoneFieldWrapper';
import CheckboxField from 'components/CheckboxFieldGroup/CheckboxField';
import { formatPhoneNumber, getPhone } from 'phoneNumberUtils';
import TrashIcon from 'icons/Trash.icon';
import EmailField from 'components/EmailField';

import './ManageDraftContactDrawer.styles.scss';

type ManageDraftContactDrawerProps = {
  className?: string;
  contact?: DraftContact;
  onSuccess: (contact: DraftContact) => void;
  onDelete: (id: string) => void;
  onClose?: () => void;
};

const ManageDraftContactDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageDraftContactDrawerProps
> = (props, ref) => {
  const { className, contact, onSuccess, onDelete, onClose } = props;

  const { _id } = contact || {};
  const classes = classNames('manage-draft-contact-drawer', className);

  const formRef = useRef<FormApi<DraftContact, Partial<DraftContact>>>(null);

  const [drawer, drawerRef] = useCallbackRef<SideDrawerRef>(null);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const { popServerError } = useContext(PopNotificationsContext);

  const commonValidations = useValidations();
  const { t } = useTranslation();

  const handleOnClose = useCallback(() => {
    drawer?.setOpen(false);
    formRef.current?.restart();
    formRef.current?.reset();
    onClose?.();
  }, [drawer, onClose]);

  const initialValues = useMemo(
    () =>
      contact
        ? {
            firstName: contact.firstName,
            lastName: contact.lastName,
            email: contact.email,
            companyName: contact.companyName,
            title: contact.title,
            preApproved: contact.preApproved || false,
            ...(contact?.phoneNumber && {
              phoneNumber: getPhone(contact.phoneNumber),
            }),
          }
        : { preApproved: false },
    [contact],
  );

  const onSubmit = useCallback(
    async ({ phoneNumber: phone, ...rest }: DraftContact) => {
      try {
        const { phoneNumber, countryCode } = phone || {};

        const values = {
          ...contact,
          ...rest,
          _id,
          ...(countryCode && {
            phoneNumber: phoneNumber
              ? formatPhoneNumber(countryCode + phoneNumber)
              : null,
          }),
        };

        onSuccess(values);
        handleOnClose();
      } catch (e) {
        popServerError(e);
      }
    },
    [contact, _id, onSuccess, handleOnClose, popServerError],
  );

  return (
    <SideDrawer ref={drawerRef} className={classes} closeAction={handleOnClose}>
      {!contact ? (
        <Loader />
      ) : (
        <Form
          initialValues={initialValues}
          onSubmit={onSubmit}
          render={({ submitting, pristine, form, handleSubmit }) => {
            formRef.current = form;

            return (
              <form onSubmit={handleSubmit}>
                <p>{t('editAContact')}</p>

                <div>
                  <Field
                    name="firstName"
                    label={t('firstName')}
                    component={InputField}
                    required
                    showInitialError
                    validate={commonValidations.name}
                  />
                  <Field
                    name="lastName"
                    label={t('lastName')}
                    component={InputField}
                    required
                    showInitialError
                    validate={commonValidations.name}
                  />
                </div>

                <EmailField required showInitialError />

                <PhoneFieldWrapper name="phoneNumber" showInitialError />

                <Field
                  name="companyName"
                  label={t('company')}
                  component={InputField}
                />

                <Field
                  name="title"
                  label={t('professionalTitle')}
                  component={InputField}
                />

                <Field
                  name="preApproved"
                  label={t('preApprovedDescription')}
                  type="checkbox"
                  component={CheckboxField}
                />

                <div className="actions">
                  <Button
                    type="button"
                    variant="link"
                    className="warning-action-button"
                    icon={TrashIcon}
                    onClick={() => {
                      onDelete(_id);
                      handleOnClose();
                    }}
                  >
                    {t('delete')}
                  </Button>
                  <Button type="submit" disabled={submitting || pristine}>
                    {t('updateContact')}
                  </Button>
                </div>
              </form>
            );
          }}
        />
      )}
    </SideDrawer>
  );
};

export default forwardRef(ManageDraftContactDrawer);
